import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ClientShortProfile, GlobalAccount, GUID, PersonOrgType } from '@ndvr/customer-api';

import { Sheet, SheetBody, SheetContent, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import {
    formatDate,
    getClientName,
    getClientTypeAlias,
    getPersonOrgName,
    stateAbbreviationMap,
    titleCase,
    useHouseholdRoutePaths,
} from 'utils';
import { useHouseholdMap } from 'views/household/household-queries';
import {
    Button,
    HouseholdLogoCard,
    PersonLogoCard,
    PersonOrgIcon,
    PersonOrgLogoCard,
    PortfolioLogoCard,
    Text,
    Title,
    VerticalLayout,
} from 'widgets';
import { Avatar } from 'widgets/Avatar';
import { NotFoundTile } from 'widgets/drawer/NotFoundTile';

import { useClientProfilesForHousehold } from './client-queries';

export const ClientDetailsDrawer = ({
    clientShortProfile,
    onClose,
}: {
    clientShortProfile: ClientShortProfile;
    onClose: () => void;
}) => {
    const { OwnersTabRoutePath, PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const globalAccountGUID = clientShortProfile.globalAccountGUID;
    const { data: clientProfiles } = useClientProfilesForHousehold(globalAccountGUID);

    const portfolioProfiles = useMemo(
        () => (clientProfiles || []).map((clientProfile) => clientProfile.portfolioProfiles).flat(),
        [clientProfiles],
    );

    const hasClientProfiles = clientProfiles && clientProfiles?.length > 0;
    const hasPortfolioProfiles = portfolioProfiles?.length > 0;

    return (
        <Sheet defaultOpen onExitComplete={onClose}>
            <SheetContent aria-describedby={undefined} layout="scroll-body" size="sm">
                <SheetTitle className="sr-only">{getPersonOrgName(clientShortProfile)}</SheetTitle>
                <SheetBody>
                    <Section $columnCenter $rowCenter $gap={24}>
                        <Avatar size={96}>
                            <PersonOrgIcon personOrgType={clientShortProfile.personOrgType} size="large" />
                        </Avatar>
                        <Title modal>{getPersonOrgName(clientShortProfile)}</Title>
                    </Section>
                    <Separator />
                    <Section $gap={16}>
                        <Title section>Details</Title>
                        <Detail title="GUID" value={globalAccountGUID} />
                        <Detail
                            title={getClientTypeAlias(clientShortProfile.personOrgType) + ' Type'}
                            value={titleCase(clientShortProfile.personOrgSubtype) || '-'}
                        />
                        <Detail
                            title="State"
                            value={stateAbbreviationMap.get(clientShortProfile?.primaryAddress?.state ?? '') ?? '-'}
                        />
                        <Detail title="Start Date" value={formatDate(clientShortProfile.startDate)} />
                    </Section>
                    <Separator />
                    <Section $gap={24}>
                        <Title modal>Household</Title>
                        <AccountItem globalAccountGUID={globalAccountGUID} />
                    </Section>
                    <Separator />
                    <Section $gap={16}>
                        <Title section>Account Owners</Title>
                        {!hasClientProfiles ? <NotFoundTile title="No account owners defined" /> : null}
                        {(clientProfiles ?? []).map((clientProfile) =>
                            clientProfile.clientPersonOrgProfile.personOrg.personOrgType === PersonOrgType.PERSON ? (
                                <PersonLogoCard
                                    key={clientProfile.client.personOrgGUID}
                                    personInfo={clientProfile.clientPersonOrgProfile.personOrg}
                                />
                            ) : (
                                <PersonOrgLogoCard
                                    key={clientProfile.clientPersonOrgProfile.personOrg.personOrgGUID}
                                    name={getClientName(clientProfile)}
                                    personOrgType={clientProfile.clientPersonOrgProfile.personOrg.personOrgType}
                                    to={OwnersTabRoutePath(clientProfile.client.globalAccountGUID)}
                                />
                            ),
                        )}
                    </Section>
                    <Separator />
                    <Section $gap={16}>
                        <Title section>Accounts</Title>
                        {!hasPortfolioProfiles ? <NotFoundTile title="No accounts defined" /> : null}
                        {portfolioProfiles.map((portfolioProfile) => (
                            <PortfolioLogoCard
                                key={portfolioProfile.portfolio.portfolioGUID}
                                name={portfolioProfile.portfolio.name.plainText}
                                portfolioTaxType={
                                    portfolioProfile.portfolio.investmentSetup.portfolioTaxProfile.portfolioTaxType
                                }
                                to={PortfolioPageRoutePath(
                                    portfolioProfile.portfolio.globalAccountGUID,
                                    portfolioProfile.portfolio.accountGroupGUID,
                                )}
                            />
                        ))}
                    </Section>
                </SheetBody>
                <SheetFooter variant="full-width">
                    <ButtonSection globalAccountGUID={globalAccountGUID} />
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

const Section = styled(VerticalLayout)`
    position: relative;
    padding: 1.5rem;
`;

const Separator = styled.div`
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.border.light};
`;

const Detail = ({ title, value }: { title: string; value: string }) => {
    return (
        <DetailLayout>
            <Text body>{title}</Text>
            <Text body>{value}</Text>
        </DetailLayout>
    );
};

const DetailLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    & > *:last-child {
        text-align: right;
    }
`;

const AccountItem = ({ globalAccountGUID }: { globalAccountGUID: GUID<GlobalAccount> }) => {
    const { PortfolioOverviewTabRoutePath } = useHouseholdRoutePaths();
    const householdMap = useHouseholdMap();
    const account = householdMap.get(globalAccountGUID);
    return (
        <HouseholdLogoCard
            name={account?.globalAccountNamePlain || ''}
            to={PortfolioOverviewTabRoutePath(globalAccountGUID)}
        />
    );
};

const ButtonSection = ({ globalAccountGUID }: { globalAccountGUID: string }) => {
    const navigate = useNavigate();
    const { OwnersTabRoutePath } = useHouseholdRoutePaths();

    return (
        <VerticalLayout>
            <Separator />
            <ButtonLayout>
                <Button
                    buttonType="secondary"
                    size="large"
                    onClick={() => navigate(OwnersTabRoutePath(globalAccountGUID))}
                >
                    View
                </Button>
            </ButtonLayout>
        </VerticalLayout>
    );
};

const ButtonLayout = styled(VerticalLayout)`
    padding: 1.5rem;
`;
